import { useEffect, useMemo, useRef, useState } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { enviroment } from "../enviroment";
import { productPendingPollDelay } from "../utils/functions/productPendingPoll";

// Bounded re-read of the price endpoint(s) behind a "Prices on their way"
// view (flag REACT_APP_PRODUCT_PENDING_ENABLED).
//
// `queryKeys` are the EXACT react-query keys whose fetchers are the price
// endpoints the view is reading: the logo popup's detail entry
// (["logos", actAs, id] -> GetLogo), the design popup's (["designs", actAs,
// id] -> GetDesign), the cart's per-line entries that useCartSync owns
// (same two shapes). Nothing else is ever refetched - never a list query.
// While `pending` is true the hook invalidates those keys with
// refetchType "all" (bypasses staleTime and the global refetchOnMount:false,
// same idiom as useHealPendingHighRes) on the 3/8/15 s schedule, at most
// MAX_PRODUCT_PENDING_POLLS times per view; the budget restarts when the
// set of keys changes (another logo opened, a line added to the cart) and
// the loop stops the moment `pending` flips false or the view unmounts.
// The re-armed tick after every poll makes the schedule independent of the
// data identity: an API that still answers "pending" with structurally
// identical data would otherwise never re-run the effect.
//
// `delaysMs` (optional; "upload stands alone", flag
// REACT_APP_UPLOAD_PENDING_ENABLED): a caller-owned schedule whose LENGTH is
// its budget, instead of the shared 3/8/15 s plan capped at 3 - the editor's
// upload re-read uses 2/2/3/3 s (4 calls). Everything else (key identity,
// budget restart, stop rules) is the same. Omitted = the plan as before.
//
// Flag off: no timer, no invalidation - the hook is inert (it must still be
// called unconditionally so hook order never varies).
export function useProductPendingPoll(
  queryKeys: readonly QueryKey[] | undefined,
  pending: boolean,
  delaysMs?: readonly number[]
): void {
  const queryClient = useQueryClient();
  const attempt = useRef(0);
  const [tick, setTick] = useState(0);
  // Flag cloneInstantEnabled: the "Preparing logo…" views (popup / editor /
  // cart) re-read on the same schedule, so the hook is armed on that flag
  // too; callers only ever pass pending=true for a state their own flag
  // gates, so with both off nothing is scheduled.
  const enabled =
    enviroment.productPendingEnabled || enviroment.cloneInstantEnabled;

  // Stable identity for the key set (callers rebuild the array every
  // render). JSON is fine: keys are strings/numbers only.
  const keysSignature = useMemo(
    () => (queryKeys && queryKeys.length > 0 ? JSON.stringify(queryKeys) : ""),
    [queryKeys]
  );

  // The budget restarts only when a key we have NOT been polling appears
  // (another logo opened, a line added to the cart). A key that merely
  // leaves the set - one cart line's product landed while another is still
  // pending - keeps the running budget, otherwise every settled line would
  // hand the remaining ones three fresh polls (N lines -> up to N+2 calls
  // for the last one instead of 3).
  const polledKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    const keys =
      keysSignature === "" ? [] : (JSON.parse(keysSignature) as QueryKey[]);
    const signatures = keys.map((key) => JSON.stringify(key));
    const hasNewKey = signatures.some((s) => !polledKeys.current.has(s));
    polledKeys.current = new Set(signatures);
    if (hasNewKey) attempt.current = 0;
  }, [keysSignature]);

  // The caller's own schedule (see the doc comment) - a joined string so a
  // constant array rebuilt per render never re-arms the effect.
  const delaysSignature = delaysMs ? delaysMs.join(",") : "";

  useEffect(() => {
    if (!enabled) return;
    if (!pending || keysSignature === "") return;
    const ownDelays =
      delaysSignature === "" ? undefined : delaysSignature.split(",").map(Number);
    const delay = ownDelays
      ? attempt.current < ownDelays.length
        ? ownDelays[attempt.current]
        : undefined
      : productPendingPollDelay(
          attempt.current,
          enviroment.productPendingPollMs
        );
    if (delay === undefined) return;
    const keys = JSON.parse(keysSignature) as QueryKey[];
    const timer = setTimeout(() => {
      attempt.current += 1;
      keys.forEach((queryKey) => {
        void queryClient.invalidateQueries({
          queryKey,
          exact: true,
          refetchType: "all",
        });
      });
      setTick((t) => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [enabled, pending, keysSignature, delaysSignature, tick, queryClient]);
}
